/**
 * FocalCompanyFinancialPanel — read-only financial snapshot card.
 *
 * Renders below the selected-company card in Step 1 of the case wizard.
 * Pulls latest-period revenue / margins / FCF from the backend's
 * /case-discovery/companies/{key}/financial-snapshot endpoint.
 *
 * Empty state: shows a "Run Company Research" CTA linking to /companies/{id}
 * (same tab) when no financial data is available.
 *
 * IMPORTANT: this is display-only. It does NOT call setCompanyData() with
 * the financials — they're derivable and stale-prone, so the wizard store
 * stays unchanged.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, TrendingUp, ExternalLink, Loader2 } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { api } from '../../lib/api';
import { formatCurrencyValue, regionFromCountry } from '../financial/utils/formatters';

interface FocalCompanyFinancialPanelProps {
  companyKey: string | null | undefined;
  companyId?: string | null;
  /**
   * Display name for the company. Sent to the backend so that synthetic /
   * slugified keys (e.g. ind_hindustan_unilever_limited) can fall back to
   * a name match against existing :Company nodes — otherwise the panel
   * incorrectly shows "no financial data" for companies that DO exist in
   * Company Research under a different companyId.
   */
  companyName?: string | null;
  /**
   * Country selected in Step 1 ('US' | 'IN' | 'UK'). Drives currency symbol
   * and unit (₹ Cr / $ M / £ M) for the financial values. Falls back to
   * the country returned by the backend snapshot when not supplied.
   */
  country?: string | null;
  /**
   * Optional callback fired when the backend successfully resolves the
   * company to an existing :Company node. Used by Step 1 to auto-populate
   * manual-entry fields (industry, ticker, real companyId) so the user
   * doesn't have to re-type data we already have in Company Research.
   */
  onCompanyResolved?: (resolved: {
    companyId?: string | null;
    name?: string | null;
    sector?: string | null;
    country?: string | null;
    ticker?: string | null;
  }) => void;
}

interface FinancialSnapshotResponse {
  companyKey: string;
  company: {
    companyId?: string | null;
    name?: string | null;
    country?: string | null;
    sector?: string | null;
    ticker?: string | null;
  } | null;
  financials: {
    period?: string | null;
    revenue?: number | null;
    netIncome?: number | null;
    grossMargin?: number | null;
    operatingMargin?: number | null;
    freeCashFlow?: number | null;
    roe?: number | null;
    source?: string | null;
  } | null;
}

// Note: formatLargeNumber is kept as a fallback when no region/country info
// is available. The currency-aware path uses formatCurrencyValue() from the
// shared formatters so symbol + unit follow Step 1's country selection.
function formatLargeNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  // Cap at 2 decimals; strip trailing zeros via simple rounding so 26.7888... → 26.79
  // and 25 → 25 (not 25.00).
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}%`;
}

const FocalCompanyFinancialPanel: React.FC<FocalCompanyFinancialPanelProps> = ({
  companyKey,
  companyId,
  companyName,
  country,
  onCompanyResolved,
}) => {

  const { data, isLoading, isError } = useQuery<FinancialSnapshotResponse>({
    queryKey: ['case-context', 'financial-snapshot', companyKey, companyName],
    queryFn: () =>
      api.get(
        `/case-discovery/companies/${encodeURIComponent(companyKey ?? '')}/financial-snapshot`,
        companyName ? { name: companyName } : undefined,
      ),
    enabled: !!companyKey,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  // Bubble the resolved Company Research record up to the parent so manual-entry
  // fields can be auto-populated. Fires only once per resolution per (id+name)
  // pair to avoid update loops.
  const lastFiredKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!onCompanyResolved) return;
    const resolved = data?.company;
    if (!resolved?.companyId) return;
    const key = `${resolved.companyId}::${resolved.sector ?? ''}::${resolved.ticker ?? ''}`;
    if (lastFiredKeyRef.current === key) return;
    lastFiredKeyRef.current = key;
    onCompanyResolved(resolved);
  }, [data?.company, onCompanyResolved]);

  // Navigation target for the "Run Company Research" / "View Company Research" CTAs.
  // Prefer the REAL companyId returned by the backend's name fallback over the
  // wizard's synthetic key (which would 404 on /companies/{key}). When the
  // backend resolved nothing, route to the company list page with a search query
  // so the user can locate or start research, rather than hitting a dead URL.
  const resolvedRealId = data?.company?.companyId || (companyId && !companyId.startsWith('manual_') ? companyId : null);
  // Open Company Research in a NEW TAB (these CTAs render an ExternalLink icon) so
  // the in-progress Discovery wizard is not unmounted. The Discovery case is only
  // persisted at Step 5, so a hard SPA navigation here would abandon the wizard and
  // the case would never be created / listed.
  const navigateToResearch = () => {
    let url = '/companies/new';
    if (resolvedRealId) {
      url = `/companies/${resolvedRealId}`;
    } else if (companyName) {
      url = `/companies?search=${encodeURIComponent(companyName)}`;
    }
    window.open(url, '_blank', 'noopener');
  };

  if (!companyKey) return null;

  if (isLoading) {
    return (
      <Card className="p-4 mt-3 flex items-center gap-2 text-neutral-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading financial snapshot…</span>
      </Card>
    );
  }

  const fin = data?.financials;
  const hasAnyData =
    fin &&
    (fin.revenue !== null && fin.revenue !== undefined) ||
    (fin?.grossMargin !== null && fin?.grossMargin !== undefined) ||
    (fin?.operatingMargin !== null && fin?.operatingMargin !== undefined);

  // Region used for currency formatting. Prefer the country passed in from
  // Step 1 (authoritative — user explicitly selected it). Fall back to the
  // backend-resolved company.country, then to the financial source. When all
  // three are missing we render the bare number.
  const region = regionFromCountry(
    country ?? data?.company?.country,
    data?.financials?.source,
  );
  const renderRevenue = (v: number | null | undefined): string =>
    region ? formatCurrencyValue(v, region, /* compact */ true) : formatLargeNumber(v);

  // Empty-state: company isn't in Company Research yet
  if (isError || !hasAnyData) {
    return (
      <Card className="p-4 mt-3 border-dashed border-neutral-300 bg-neutral-50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-neutral-700">
                No financial data loaded for this company yet
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                Run Company Research to unlock revenue, margins, and peer benchmarking
                in later steps.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={navigateToResearch}
            className="flex items-center gap-1 flex-shrink-0"
          >
            Run Company Research
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 mt-3">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-primary-600" />
        <h4 className="text-sm font-semibold text-neutral-900">
          Latest Financial Snapshot
        </h4>
        {fin?.period && (
          <span className="text-xs text-neutral-500">({fin.period})</span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-neutral-500">Revenue</p>
          <p className="text-lg font-semibold text-neutral-900">
            {renderRevenue(fin?.revenue)}
          </p>
        </div>
        <div>
          <p className="text-xs text-neutral-500">Gross Margin</p>
          <p className="text-lg font-semibold text-neutral-900">
            {formatPercent(fin?.grossMargin)}
          </p>
        </div>
        <div>
          <p className="text-xs text-neutral-500">Operating Margin</p>
          <p className="text-lg font-semibold text-neutral-900">
            {formatPercent(fin?.operatingMargin)}
          </p>
        </div>
        <div>
          <p className="text-xs text-neutral-500">ROE</p>
          <p className="text-lg font-semibold text-neutral-900">
            {formatPercent(fin?.roe)}
          </p>
        </div>
      </div>
      {resolvedRealId && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <button
            onClick={navigateToResearch}
            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            View full Company Research
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}
    </Card>
  );
};

export default FocalCompanyFinancialPanel;
